import React, { useMemo, useState } from "react";

import "./LineGraphStyles.scss";
import "../ChartFrame.scss";
import ChartTooltip from "../ChartTooltip";
import { withAlpha } from "../chartPalette";
import { CHART_THEME } from "../chartTheme";
import {
  formatTick,
  linearScale,
  polylineAreaPath,
  polylinePath,
  wrapLabel,
} from "../svg/helpers";

export default function LineGraph({
  graphData,
  chartHeight = 500,
  compact = false,
}) {
  const [hover, setHover] = useState(null);

  const { categories, series, width, height, margin } = useMemo(() => {
    const cats = [];
    const seriesList = [];
    const surveys = Array.isArray(graphData) ? graphData : [];
    surveys.forEach((survey, index) => {
      const values = [];
      const milData = Array.isArray(survey?.mil_data) ? survey.mil_data : [];
      milData.forEach((data) => {
        values.push(Number(data?.mil) || 0);
        if (index === 0) cats.push(data?.domain_name || "");
      });
      seriesList.push({
        name: survey?.name || `Series ${index + 1}`,
        // Single consistent color for all trend series (cobalt).
        color: "#0058be",
        values,
      });
    });

    const w = compact ? 1040 : 1100;
    const h = Math.min(chartHeight || 500, compact ? 280 : 360);
    const m = {
      top: compact ? 12 : 20,
      right: 24,
      bottom: compact ? 78 : 90,
      left: compact ? 36 : 44,
    };
    return { categories: cats, series: seriesList, width: w, height: h, margin: m };
  }, [graphData, chartHeight, compact]);

  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  const xScale = linearScale(0, Math.max(categories.length - 1, 1), 0, plotW);
  // Maturity score is always on the 0–5 MIL scale (client requirement), so the Y
  // axis is fixed at 0–5 with integer ticks regardless of the data range. This
  // keeps maturity comparable across assessments — previously the axis auto-scaled
  // to the data, which filled the frame but changed scale from chart to chart.
  const yMax = 5;
  const yTicks = [0, 1, 2, 3, 4, 5];
  const yDecimals = 0;
  const yScale = linearScale(0, yMax, plotH, 0);
  // Left padding in the viewBox so the first rotated x-axis label is not
  // clipped at the frame edge.
  const padL = 44;

  const seriesPoints = series.map((s) => ({
    ...s,
    points: s.values.map((v, i) => ({
      x: xScale(i),
      y: yScale(v),
      value: v,
      category: categories[i],
    })),
  }));

  return (
    <div className="progress-graph cb-chart-frame cb-chart-frame--line">
      {!compact && (
        <p className="cb-line-title">Progress Graph</p>
      )}
      <svg
        viewBox={`${-padL} 0 ${width + padL} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Maturity progress by domain"
      >

        <g transform={`translate(${margin.left},${margin.top})`}>
          {yTicks.map((t) => (
            <g key={t}>
              <line
                className="cb-chart-frame__grid"
                x1={0}
                x2={plotW}
                y1={yScale(t)}
                y2={yScale(t)}
                stroke={CHART_THEME.grid}
                strokeDasharray="4 6"
              />
              <text
                className="cb-chart-frame__axis-label"
                x={-10}
                y={yScale(t) + 4}
                textAnchor="end"
              >
                {formatTick(t, yDecimals)}
              </text>
            </g>
          ))}

          {seriesPoints.map((s, si) => (
            <g key={s.name}>
              <path
                d={polylineAreaPath(s.points, plotH)}
                fill={withAlpha(s.color, 0.08)}
              />
              <path
                d={polylinePath(s.points)}
                fill="none"
                stroke={s.color}
                strokeWidth={CHART_THEME.lineWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {s.points.map((p, pi) => (
                <circle
                  key={`${si}-${pi}`}
                  cx={p.x}
                  cy={p.y}
                  r={hover?.si === si && hover?.pi === pi ? 6.5 : 5}
                  fill="#fff"
                  stroke={s.color}
                  strokeWidth={2.5}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget
                      .closest(".cb-chart-frame")
                      ?.getBoundingClientRect();
                    setHover({
                      si,
                      pi,
                      x: e.clientX - (rect?.left || 0),
                      y: e.clientY - (rect?.top || 0),
                      category: p.category,
                      name: s.name,
                      value: p.value,
                      color: s.color,
                    });
                  }}
                  onMouseLeave={() => setHover(null)}
                />
              ))}
            </g>
          ))}

          {categories.map((cat, i) => {
            const lines = wrapLabel(cat, 13);
            return (
              <text
                key={cat + i}
                className="cb-chart-frame__tick-label"
                x={xScale(i)}
                y={plotH + 16}
                textAnchor="middle"
              >
                {lines.map((ln, li) => (
                  <tspan key={li} x={xScale(i)} dy={li === 0 ? 0 : 11}>
                    {ln}
                  </tspan>
                ))}
              </text>
            );
          })}
        </g>
      </svg>

      {series.length > 0 && (
        <div className="cb-chart-frame__legend">
          {series.map((s) => (
            <span key={s.name} className="cb-chart-frame__legend-item">
              <span
                className="cb-chart-frame__swatch"
                style={{ background: s.color }}
              />
              {s.name}
            </span>
          ))}
        </div>
      )}

      <ChartTooltip x={hover?.x || 0} y={hover?.y || 0} visible={!!hover}>
        {hover && (
          <>
            <span className="cb-chart-tooltip__title">{hover.category}</span>
            <span className="cb-chart-tooltip__row">
              <span
                className="cb-chart-tooltip__dot"
                style={{ background: hover.color }}
              />
              {hover.name}: <strong>MIL {hover.value}</strong>
            </span>
          </>
        )}
      </ChartTooltip>
    </div>
  );
}
